// scheduling/pages/IDDReview.jsx
// Data Integrity -> Budgets & Dates: managers set each job's budget and internal due dates
// (IDD start / due) for the coming season, then submit them to Practice Engine.
//
// Guided flow (Hutson, 2026-10-19): 1 Budgets -> 2 Dates -> 3 Submit. Every decision saves
// right away as ONE half of the job's draft (save_draft `parts`); a job is ready when both
// halves are decided (utils/iddSteps.js, mirroring the server's "reviewed" rule). Budgets
// get a model suggestion for returning 1065 / 1120 / 1120-S clients (utils/mlBudget.js,
// return_job_ml contract v1.2.1), else last year's hours; dates get Claude's suggestion
// (api/rprsIDD/aiSchedule.js), pre-computed before the cycle opens.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { fetchIDDJobs, fetchDataIntegrityCycles, fetchStaff, fetchJobTaskBudgets, fetchJobTaskBudgetsBatch, fetchJobPredictions, fetchJMWeeklyHoursPY, fetchJobWeeklyWIP, fetchNonBillProgressSummary } from '../services/schedData';
import { saveIDDDraft, submitIDDReview, runAIRecommend, fetchAILatest, clearIDDDraft, removeIDDJob } from '../services/schedApi';
import {
  ML_STATE_META, classifyBudget, thisYearLabel, pyFinalBudget, pyInScopeActual,
  splitPrediction, effectiveBudgetOf, mlNotes, isStubPrediction, hasFlag, fold199AIntoPrep,
} from '../utils/mlBudget';
import {
  STEPS, budgetDecided, datesDecided, jobReady, missingParts, stepCounts, firstOpenStep,
  pyBudgetSuggestion, budgetOptions, datesOptions, aiHoursStale, budgetSource, datesSource, isOOSTask,
  recommendedPlan, describePlan, undoable, extensionCutoff, datesStepLocked, lastYearEmpty,
} from '../utils/iddSteps';
import IDDHistoryModal from '../components/IDDHistoryModal';
import { useUrlState } from '../../hooks/useUrlState';
import { useTaskBudgetCache } from '../hooks/useTaskBudgetCache';
import IDDTimeline from '../components/IDDTimeline';
import IDDHistogram from '../components/IDDHistogram';
import DataIntegrityAdminTab from '../components/DataIntegrityAdminTab';
import IDDHelpModal from '../components/IDDHelpModal';
import NonBillBudgetTab from '../components/NonBillBudgetTab';
import IDDStepBar from '../components/idd/IDDStepBar';
import IDDBudgetsTable from '../components/idd/IDDBudgetsTable';
import IDDDatesTable from '../components/idd/IDDDatesTable';
import IDDSubmitPanel from '../components/idd/IDDSubmitPanel';
import IDDBulkBar from '../components/idd/IDDBulkBar';
import { fmtDate } from '../components/idd/iddFormat';

// ── AI loading jokes ────────────────────────────────────────────────────────
const AI_JOKES = [
  "Consulting the crystal ball of deadlines...",
  "Teaching AI the art of procrastination management...",
  "Calculating the optimal number of extensions...",
  "Asking Claude if April 15th can be moved to June...",
  "Running 10,000 simulations of 'just one more day'...",
  "Optimizing schedules faster than you can say 'busy season'...",
  "Convincing the algorithm that weekends exist...",
  "Balancing workloads like a CPA balances books...",
  "Finding the sweet spot between 'too early' and 'way too late'...",
  "Crunching numbers so you don't have to crunch time...",
  "Scheduling magic in progress... no rabbits were harmed...",
  "Negotiating with the calendar gods...",
  "Proving that AI is cheaper than overtime...",
  "Aligning the stars and the deadlines...",
  "Making busy season slightly less busy...",
];

const LOADER_GIF = 'https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/rprs_kb_calendar_loader.apng';
const SAVE_CHUNK = 100;    // reviews per save_draft call (set-based: ~1s per 100 jobs, 2026-10-20)

// job_predictions rows -> { [PEJobIndex]: prediction }. Tolerates a failed read ([]).
function indexPredictions(rows) {
  const map = {};
  for (const p of (Array.isArray(rows) ? rows : [])) map[p.PEJobIndex] = p;
  return map;
}

// Every editable task's remaining hours: the typed value, else the saved draft, else PE.
function fullTaskMap(rows, typed = {}) {
  const map = {};
  for (const t of rows || []) {
    if (isOOSTask(t)) continue;
    const orig = (Number(t.BudgetHours) || 0) - (Number(t.WIPHours) || 0);
    const v = typed[t.TaskSubject] != null ? typed[t.TaskSubject] : t.SavedRemainingHrs != null ? t.SavedRemainingHrs : orig;
    map[t.TaskSubject] = Math.max(0, Number(v) || 0);
  }
  return map;
}

const withoutKey = (obj, key) => {
  if (!obj || !(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
};

// "Tax years starting Jan 1 – Dec 31, 2026 · ending by Sep 30, 2027 · AI dates through May 15, 2027"
// Fall 2026 (DJB, 2026-10-20): "Tax years ending Sep 30, 2026 – May 31, 2027 (any start
// date) · AI dates through Jun 30, 2027". Says "any start date" whenever the cycle has no
// period-start filter, so fiscal years that began in an earlier year read as included.
function cycleSummary(c) {
  if (!c) return null;
  const parts = [];
  const span = (from, to) => (from && to ? `${fmtDate(from)} – ${fmtDate(to)}` : from ? `from ${fmtDate(from)}` : `by ${fmtDate(to)}`);
  const hasStart = !!(c.PeriodStartFrom || c.PeriodStartTo);
  if (c.PeriodEndFrom || c.PeriodEndTo) parts.push(`Tax years ending ${span(c.PeriodEndFrom, c.PeriodEndTo)}${hasStart ? '' : ' (any start date)'}`);
  if (hasStart) parts.push(`starting ${span(c.PeriodStartFrom, c.PeriodStartTo)}`);
  const ext = extensionCutoff(c.ScheduleUpTo);
  if (c.ScheduleUpTo) parts.push(`AI dates through ${fmtDate(c.ScheduleUpTo)}${ext ? ` (likely extensions to ${fmtDate(ext)})` : ''}`);
  return parts.join(' · ');
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function IDDReview() {
  const { isSuperUser, isDevSuperUser, email, username, ready } = useAuth();
  // AppShell exposes refreshIDDProgress so this page can ping the top-bar pulse /
  // subtab indicator after every mutation. Falls through when mounted outside AppShell.
  const { refreshIDDProgress } = useOutletContext() || {};

  // Data
  const [jobs, setJobs] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pyWeeklyData, setPyWeeklyData] = useState([]);
  const [error, setError] = useState(null);

  // URL state: ?subtab, ?step, ?show, ?ml, ?q, ?mgr, ?office, ?dept (shareable / navbot
  // deeplinks). earnings/admin are super-only; the render gates them.
  const [subTab, setSubTab] = useUrlState('subtab', 'idd', { validate: ['idd', 'nonbill', 'earnings', 'admin'] });
  const [step, setStep] = useUrlState('step', '', { validate: ['', ...STEPS] });
  const [show, setShow] = useUrlState('show', 'todo', { validate: ['todo', 'done', 'all'] });
  const [mlFilter, setMlFilter] = useUrlState('ml', 'all', {
    validate: ['all', 'outside', 'stale', 'no-budget', 'set-this-year', 'close', 'agrees', 'none'],
  });
  const [cycleName, setCycleName] = useState('default');
  const [search, setSearch] = useUrlState('q', '');
  const [managerFilter, setManagerFilter] = useUrlState('mgr', 'mine');
  const [officeFilter, setOfficeFilter] = useUrlState('office', 'all');
  const [deptFilter, setDeptFilter] = useUrlState('dept', 'all');
  // The picked cycle lives in the URL (2026-10-21): it used to be page memory only, so any
  // reload or shared link fell back to "No cycle" while none was open.
  const [cycleParam, setCycleParam] = useUrlState('cycle', '');
  const initialCycleParam = useRef(cycleParam);
  const [sortCol, setSortCol] = useState('clientCode');
  const [sortDir, setSortDir] = useState('asc');

  const [selectedJob, setSelectedJob] = useState(null);     // expanded (budgets) / selected (dates) job
  const [hoveredJob, setHoveredJob] = useState(null);       // table row <-> timeline bar
  const [focusJobPYWeekly, setFocusJobPYWeekly] = useState([]);
  const [selection, setSelection] = useState(() => new Set());
  const [timelineZoom, setTimelineZoom] = useState(1);
  // 'current' = today -> cutoff; 'year' = cutoff - 52 weeks -> cutoff (PY context).
  const [chartWindowMode, setChartWindowMode] = useState('current');
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpAutoShown, setHelpAutoShown] = useState(false);

  // Unsaved typing only: { [pe]: { tasks?: {subject: remaining}, startDeadline?, deadline? } }.
  // Buttons (Use model / Keep / Accept ...) save straight away and never sit here.
  const [edits, setEdits] = useState({});
  // How each half was decided THIS session (the row says "model", "kept", "AI" ...):
  // { budget: {pe: kind}, dates: {pe: kind} }. After a reload a decided row just shows ✓.
  const [kinds, setKinds] = useState({ budget: {}, dates: {} });
  const [busy, setBusy] = useState(null);                   // a save in flight (label) or null
  const [myStaffId, setMyStaffId] = useState(null);

  // Model-suggested budgets: { [PEJobIndex]: prediction }, failing soft to {} (the page
  // then offers last year's hours instead).
  const [predictions, setPredictions] = useState({});

  // Per-task budgets for the jobs in view: one batched call per 500 jobs, only for ids not
  // cached yet (hooks/useTaskBudgetCache.js; see its note on the 2026-10-19 loop).
  const { taskCache, ensureTasks, resetTaskCache } = useTaskBudgetCache(fetchJobTaskBudgetsBatch);
  // Per-job overrides after a save / clear, so one row refreshes without a full prefetch.
  const [taskCachePatch, setTaskCachePatch] = useState({});

  // Auto-open help the first time the IDD subtab is shown in a session.
  useEffect(() => {
    if (subTab === 'idd' && !helpAutoShown) { setHelpOpen(true); setHelpAutoShown(true); }
  }, [subTab, helpAutoShown]);

  const timelineScrollRef = useRef(null);
  const histogramScrollRef = useRef(null);
  const scrollSyncLock = useRef(false);
  const syncScroll = useCallback((from, to) => () => {
    if (scrollSyncLock.current) return;
    scrollSyncLock.current = true;
    if (from.current && to.current) to.current.scrollLeft = from.current.scrollLeft;
    requestAnimationFrame(() => { scrollSyncLock.current = false; });
  }, []);
  const handleTimelineScroll = useMemo(() => syncScroll(timelineScrollRef, histogramScrollRef), [syncScroll]);
  const handleHistogramScroll = useMemo(() => syncScroll(histogramScrollRef, timelineScrollRef), [syncScroll]);

  // The job the timeline + histogram highlight (Hutson, 2026-10-21): the hovered row or bar,
  // else the selected one, so sweeping the table lights each job up and leaving it falls
  // back to the selection.
  const focusJob = hoveredJob ?? selectedJob;
  // Its last-year weekly WIP (the histogram overlay). Cached per job; a hover waits 150ms
  // before fetching so sweeping down the table doesn't fire a call per row.
  const pyWeeklyCache = useRef(new Map());
  useEffect(() => {
    const job = focusJob ? jobs.find(j => j.PEJobIndex === focusJob) : null;
    const pyIdx = job?.PEJobIndex_PY;
    if (!pyIdx) { setFocusJobPYWeekly([]); return; }
    const cached = pyWeeklyCache.current.get(pyIdx);
    if (cached) { setFocusJobPYWeekly(cached); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchJobWeeklyWIP(pyIdx)
        .then(data => {
          const rows = Array.isArray(data) ? data : [];
          pyWeeklyCache.current.set(pyIdx, rows);
          if (!cancelled) setFocusJobPYWeekly(rows);
        })
        .catch(() => { if (!cancelled) setFocusJobPYWeekly([]); });
    }, focusJob === hoveredJob ? 150 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [focusJob, hoveredJob, jobs]);

  // ── Load data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const [staffData, cycleData] = await Promise.all([fetchStaff(), fetchDataIntegrityCycles()]);
        if (cancelled) return;
        setCycles(cycleData);
        const myUser = (email || username || '').toLowerCase();
        const me = staffData.find(s =>
          (s.Email || '').toLowerCase() === myUser ||
          (s.Email || '').toLowerCase().startsWith(myUser.split('@')[0])
        );
        if (me) setMyStaffId(me.StaffId);
        // The cycle in the URL, else the open one, else (nothing open: before 10-26) the newest
        // QA cycle the caller may use, so a tester never lands on "No cycle". The server only
        // lists QA cycles to developers and super users.
        const fromUrl = initialCycleParam.current && cycleData.find(c => c.CycleName === initialCycleParam.current);
        const activeCycle = cycleData.find(c => c.IsActive);
        const qaCycle = cycleData.find(c => c.IsQA);
        const cn = (fromUrl || activeCycle || qaCycle)?.CycleName || 'default';
        setCycleName(cn);
        // Predictions fail soft: a missing or failing ml read never blocks the tab.
        const [jobData, predData] = await Promise.all([
          fetchIDDJobs({ cycleName: cn }),
          fetchJobPredictions({ cycleName: cn }).catch(() => []),
        ]);
        if (cancelled) return;
        setJobs(jobData);
        setPredictions(indexPredictions(predData));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ready, email, username]);

  const reloadJobs = useCallback(async (cn) => {
    try {
      setLoading(true);
      const [jobData, predData] = await Promise.all([
        fetchIDDJobs({ cycleName: cn || cycleName }),
        fetchJobPredictions({ cycleName: cn || cycleName }).catch(() => []),
      ]);
      setJobs(jobData);
      setPredictions(indexPredictions(predData));
      setEdits({});
      setKinds({ budget: {}, dates: {} });
      setSelection(new Set());
      setTaskCachePatch({});
      resetTaskCache();
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [cycleName, resetTaskCache]);

  const reloadCycles = useCallback(async () => {
    setCycles(await fetchDataIntegrityCycles());
  }, []);

  // ── Derived: filters, cycle, scope ─────────────────────────────────────
  const managerList = useMemo(() => {
    const map = new Map();
    for (const j of jobs) if (j.ManagerStaffId && j.ManagerName) map.set(j.ManagerStaffId, j.ManagerName);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [jobs]);
  const officeList = useMemo(() => [...new Set(jobs.map(j => j.OfficeCode).filter(Boolean))].sort(), [jobs]);
  const deptList = useMemo(() => [...new Set(jobs.map(j => j.DepartmentCode).filter(Boolean))].sort(), [jobs]);

  const filteredUnsorted = useMemo(() => {
    let list = jobs;
    if (managerFilter === 'mine' && myStaffId) list = list.filter(j => j.ManagerStaffId === myStaffId);
    else if (managerFilter !== 'all' && managerFilter !== 'mine') {
      const mId = parseInt(managerFilter, 10);
      list = list.filter(j => j.ManagerStaffId === mId);
    }
    if (officeFilter !== 'all') list = list.filter(j => j.OfficeCode === officeFilter);
    if (deptFilter !== 'all') list = list.filter(j => j.DepartmentCode === deptFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(j =>
        (j.ClientCode || '').toLowerCase().includes(q) ||
        (j.ClientName || '').toLowerCase().includes(q) ||
        (j.JobName || '').toLowerCase().includes(q) ||
        (j.ManagerName || '').toLowerCase().includes(q) ||
        (j.PartnerName || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [jobs, managerFilter, myStaffId, officeFilter, deptFilter, search]);

  // QA sandbox cycles (DataIntegrityCycles.IsQA): only super + dev users see one; inside it
  // devs get the all-managers scope and Submit is a server-side dry run.
  const currentCycle = useMemo(() => cycles.find(c => c.CycleName === cycleName) || null, [cycles, cycleName]);
  const isQACycle = currentCycle?.IsQA === true;
  const canActAsAny = isSuperUser || (isQACycle && isDevSuperUser);
  const isAdminish = isSuperUser || isDevSuperUser;
  const selectedManagerId = /^\d+$/.test(String(managerFilter)) ? parseInt(managerFilter, 10) : null;
  const actingManagerId = (canActAsAny && selectedManagerId) ? selectedManagerId : myStaffId;

  // Task-budget prefetch (batched, incremental, scoped by the server).
  useEffect(() => {
    ensureTasks(filteredUnsorted.map(j => j.PEJobIndex), cycleName || 'default', taskCache);
  }, [filteredUnsorted, taskCache, cycleName, ensureTasks]);
  const tasksReady = useMemo(() => filteredUnsorted.every(j => j.PEJobIndex in taskCache), [filteredUnsorted, taskCache]);

  // ── AI dates ───────────────────────────────────────────────────────────
  const [aiRunning, setAiRunning] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState({});   // { [pe]: {startdate, enddate, Hours, ...} }
  const [aiInfo, setAiInfo] = useState(null);                // { kind, at, count }
  const [aiModel, setAiModel] = useState('');                // '' = default (Sonnet)
  const [aiJoke, setAiJoke] = useState('');
  useEffect(() => {
    if (!aiRunning) return;
    setAiJoke(AI_JOKES[Math.floor(Math.random() * AI_JOKES.length)]);
    const interval = setInterval(() => setAiJoke(AI_JOKES[Math.floor(Math.random() * AI_JOKES.length)]), 3000);
    return () => clearInterval(interval);
  }, [aiRunning]);

  // The manager's newest finished AI run (the cycle-open pre-compute, or their last refresh),
  // loaded again whenever the manager in view changes (2026-10-21: switching managers in the
  // picker kept the previous manager's dates, so the timeline and hours chart showed none
  // until a hard refresh). A run made in this session is kept per manager, so switching away
  // and back shows it straight away.
  const aiKey = cycleName && cycleName !== 'default' && actingManagerId ? `${cycleName}|${actingManagerId}` : null;
  const aiKeyRef = useRef(aiKey);
  aiKeyRef.current = aiKey;
  const sessionAI = useRef(new Map());
  useEffect(() => {
    setAiSuggestions({});
    setAiInfo(null);
    if (loading || !aiKey) return undefined;
    const mine = sessionAI.current.get(aiKey);
    if (mine) { setAiSuggestions(mine.map); setAiInfo(mine.info); return undefined; }
    let cancelled = false;
    fetchAILatest({ cycleName, managerId: actingManagerId })
      .then(r => {
        if (cancelled || !r || !Array.isArray(r.recommendations)) return;
        const map = {};
        for (const rec of r.recommendations) map[rec.Job_Idx] = rec;
        setAiSuggestions(map);
        setAiInfo({ kind: r.kind, at: r.completedAt, count: r.recommendations.length });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loading, aiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Last year's weekly hours for the hours chart: the manager in view (it was always the
  // signed-in user's, so a super user looking at Kate saw "PY Actual WIP (0h)"); none for
  // "All managers".
  const pyManagerId = managerFilter === 'all' ? null : actingManagerId;
  useEffect(() => {
    setPyWeeklyData([]);
    if (!pyManagerId) return undefined;
    let cancelled = false;
    fetchJMWeeklyHoursPY(pyManagerId).then(d => { if (!cancelled) setPyWeeklyData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [pyManagerId]);

  const handleAIRecommend = useCallback(async () => {
    setAiRunning(true);
    try {
      // Your own jobs; a super user (or a dev in a QA cycle) who picked a manager runs it for
      // that manager. The server enforces the same rule whatever is sent.
      const runKey = aiKey;
      const result = await runAIRecommend({ cycleName: cycleName || 'default', managerId: actingManagerId, model: aiModel || undefined });
      const map = {};
      for (const rec of (result.recommendations || [])) map[rec.Job_Idx] = rec;
      const info = { kind: 'interactive', at: new Date().toISOString(), count: Object.keys(map).length, hitCap: !!result.hitOutputCap };
      if (runKey) sessionAI.current.set(runKey, { map, info });
      // The manager may have been switched while it ran: show it only where it belongs.
      if (aiKeyRef.current === runKey) { setAiSuggestions(map); setAiInfo(info); }
    } catch (err) {
      alert('AI dates failed: ' + err.message);
    } finally {
      setAiRunning(false);
    }
  }, [cycleName, actingManagerId, aiModel, aiKey]);

  // ── Per-job view for the steps ─────────────────────────────────────────
  // Each job's task rows. Until its budget is decided, Section 199A is shown and saved
  // folded into Preparation (#731); once decided, the rows show what was saved.
  const taskRows = useMemo(() => {
    const out = {};
    for (const job of filteredUnsorted) {
      const pe = job.PEJobIndex;
      const rows = taskCachePatch[pe] ?? taskCache[pe];
      if (rows) out[pe] = budgetDecided(job) ? rows : fold199AIntoPrep(rows);
    }
    return out;
  }, [filteredUnsorted, taskCache, taskCachePatch]);

  // ml: the model's view of the SAVED budget (typing doesn't move a row's label).
  const mlRows = useMemo(() => {
    const out = {};
    for (const job of filteredUnsorted) {
      const pe = job.PEJobIndex;
      const pred = predictions[pe];
      const rows = taskRows[pe];
      if (!pred || !rows) continue;
      const budget = Number(effectiveBudgetOf(job, rows, {}).effectiveBudget) || 0;
      const pyFinal = pyFinalBudget(rows, job);
      out[pe] = {
        pred, stub: isStubPrediction(pred), hypothetical: hasFlag(pred, 'HYPOTHETICAL'),
        pyFinal, pyActual: pyInScopeActual(rows, pred), budget,
        thisYear: thisYearLabel(budget, pyFinal),
        state: classifyBudget({ budget, pyFinal, pred }),
        split: splitPrediction(pred.PredHours, rows),
      };
    }
    return out;
  }, [filteredUnsorted, predictions, taskRows]);

  const rowInfo = useMemo(() => {
    const out = {};
    for (const job of filteredUnsorted) {
      const pe = job.PEJobIndex;
      const rows = taskRows[pe];
      if (!rows) continue;
      const base = effectiveBudgetOf(job, rows, {});
      const eff = effectiveBudgetOf(job, rows, edits[pe]?.tasks);
      const ml = mlRows[pe] || null;
      const py = ml ? null : pyBudgetSuggestion(rows);
      const decided = budgetDecided(job);
      const dDecided = datesDecided(job);
      const baseBudget = Number(base.effectiveBudget) || 0;
      const actual = pyInScopeActual(rows, predictions[pe]);
      out[pe] = {
        rows, ml, py, decided, datesDecided: dDecided, ready: jobReady(job),
        base: baseBudget, baseRemaining: Number(base.effectiveRemaining) || 0,
        eff: Number(eff.effectiveBudget) || 0,
        decidedHours: job.ReviewStatus === 'draft' && job.NewBudgetHours != null ? Number(job.NewBudgetHours) : null,
        options: decided ? [] : budgetOptions({ budget: baseBudget, ml, py }),
        dateOptions: dDecided ? [] : datesOptions({ job, ai: aiSuggestions[pe] }),
        lastYear: { budget: pyFinalBudget(rows, job), actual: actual.hours, fromModel: actual.fromModel },
        locked: job.ReviewStatus === 'submitted' || !!job.RemoveAction,
        lastYearEmpty: lastYearEmpty(job),
      };
    }
    return out;
  }, [filteredUnsorted, taskRows, edits, mlRows, predictions, aiSuggestions]);

  // The acting manager's own population drives the steps and Submit.
  const myJobs = useMemo(() => {
    const mgr = isQACycle && canActAsAny ? actingManagerId : myStaffId;
    return mgr ? jobs.filter(j => j.ManagerStaffId === mgr) : [];
  }, [jobs, myStaffId, isQACycle, canActAsAny, actingManagerId]);
  const counts = useMemo(() => stepCounts(myJobs), [myJobs]);
  const allSubmitted = counts.total > 0 && counts.submitted === counts.total;
  const noCycle = !currentCycle;
  const readOnly = allSubmitted || noCycle;
  // Dates wait for budgets (Hutson, 2026-10-21): the step is locked until every job has a
  // budget, the first time; afterwards a job still needing one locks only its own row.
  const datesLock = useMemo(() => datesStepLocked(myJobs), [myJobs]);
  const datesReadOnly = readOnly || datesLock.locked;

  // Open on the first step with work left (once, after load).
  useEffect(() => {
    if (!step && !loading && tasksReady && myJobs.length > 0) setStep(firstOpenStep(counts));
  }, [step, loading, tasksReady, myJobs.length, counts, setStep]);
  const activeStep = step || 'budgets';

  // Budgets changed since the AI dates were made (dates still undecided).
  const staleAI = useMemo(() => {
    const s = new Set();
    for (const job of filteredUnsorted) {
      const r = rowInfo[job.PEJobIndex];
      if (!r || r.datesDecided) continue;
      if (aiHoursStale(aiSuggestions[job.PEJobIndex], { budget: r.decidedHours ?? r.base, wip: job.CYActualHours })) s.add(job.PEJobIndex);
    }
    return s;
  }, [filteredUnsorted, rowInfo, aiSuggestions]);

  // Budgets done and some changed since the AI dates were made: re-plan once with them as the
  // Dates step opens (Hutson, 2026-10-21), rather than leave "N budgets changed since" for the
  // manager to notice. Once per manager per page session, and only for the manager themself
  // (or in a QA cycle), so an admin browsing a live cycle doesn't start runs.
  const autoRerunDone = useRef(new Set());
  const [autoRerun, setAutoRerun] = useState(false);
  useEffect(() => {
    if (activeStep !== 'dates' || datesReadOnly || aiRunning || !aiKey || !aiInfo || staleAI.size === 0) return;
    if (autoRerunDone.current.has(aiKey)) return;
    if (!(isQACycle || actingManagerId === myStaffId)) return;
    autoRerunDone.current.add(aiKey);
    setAutoRerun(true);
    handleAIRecommend().finally(() => setAutoRerun(false));
  }, [activeStep, datesReadOnly, aiRunning, aiKey, aiInfo, staleAI, isQACycle, actingManagerId, myStaffId, handleAIRecommend]);

  // ── The table's rows for the active step ───────────────────────────────
  const isDone = useCallback((job) => {
    const r = rowInfo[job.PEJobIndex];
    if (!r) return false;
    return activeStep === 'dates' ? r.datesDecided : r.decided;
  }, [rowInfo, activeStep]);
  const showCounts = useMemo(() => {
    let done = 0;
    for (const j of filteredUnsorted) if (isDone(j)) done++;
    return { all: filteredUnsorted.length, done, todo: filteredUnsorted.length - done };
  }, [filteredUnsorted, isDone]);
  // "No budget" is about the budget, not the model: it counts EVERY job without one (a 1040
  // with no budget is as unbudgeted as a 1065). The other chips are the model's states.
  const hasNoBudget = useCallback((j) => (rowInfo[j.PEJobIndex]?.base ?? 1) <= 0, [rowInfo]);
  const mlCounts = useMemo(() => {
    const c = { all: filteredUnsorted.length, outside: 0, stale: 0, 'no-budget': 0, 'set-this-year': 0, close: 0, agrees: 0, none: 0 };
    for (const j of filteredUnsorted) {
      if (hasNoBudget(j)) c['no-budget']++;
      const r = mlRows[j.PEJobIndex];
      if (!predictions[j.PEJobIndex]) { c.none++; continue; }
      if (!r || r.state === 'no-budget') continue;
      c[r.state]++;
      if (ML_STATE_META[r.state]?.outside) c.outside++;
    }
    return c;
  }, [filteredUnsorted, mlRows, predictions, hasNoBudget]);
  const anyPredictions = Object.keys(predictions).length > 0;
  const stubInView = useMemo(() => Object.values(predictions).some(isStubPrediction), [predictions]);

  const tableJobs = useMemo(() => {
    let list = filteredUnsorted;
    if (show === 'todo') list = list.filter(j => !isDone(j));
    else if (show === 'done') list = list.filter(j => isDone(j));
    if (activeStep === 'budgets' && mlFilter !== 'all') {
      list = list.filter(j => {
        if (mlFilter === 'none') return !predictions[j.PEJobIndex];
        if (mlFilter === 'no-budget') return hasNoBudget(j);
        const r = mlRows[j.PEJobIndex];
        if (!r) return false;
        return mlFilter === 'outside' ? !!ML_STATE_META[r.state]?.outside : r.state === mlFilter;
      });
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = getVal(a, sortCol, rowInfo), bv = getVal(b, sortCol, rowInfo);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av || '').localeCompare(String(bv || '')) * dir;
    });
  }, [filteredUnsorted, show, isDone, activeStep, mlFilter, predictions, mlRows, sortCol, sortDir, rowInfo, hasNoBudget]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  }

  const goStep = useCallback((s, pe = null) => {
    setStep(s);
    setSelection(new Set());
    if (pe) { setShow('all'); setSelectedJob(pe); }
  }, [setStep, setShow]);

  // ── Saving decisions ───────────────────────────────────────────────────
  const saveReviews = useCallback(async (reviews, label) => {
    const cn = cycleName || 'default';
    for (let i = 0; i < reviews.length; i += SAVE_CHUNK) {
      setBusy(reviews.length > SAVE_CHUNK ? `${label} ${Math.min(i + SAVE_CHUNK, reviews.length)} of ${reviews.length}…` : `${label}…`);
      await saveIDDDraft({ cycleName: cn, reviews: reviews.slice(i, i + SAVE_CHUNK) });
    }
  }, [cycleName]);

  const patchJob = useCallback((pe, patch) => {
    setJobs(prev => prev.map(j => (j.PEJobIndex === pe ? { ...j, ...(typeof patch === 'function' ? patch(j) : patch) } : j)));
  }, []);
  const patchTasks = useCallback((pe, map) => {
    setTaskCachePatch(prev => {
      const rows = prev[pe] ?? taskCache[pe] ?? [];
      return { ...prev, [pe]: rows.map(t => ({ ...t, SavedRemainingHrs: map ? (map[t.TaskSubject] ?? t.SavedRemainingHrs) : null })) };
    });
  }, [taskCache]);
  const setKind = useCallback((half, pes, kind) => {
    setKinds(prev => {
      const next = { ...prev[half] };
      for (const pe of pes) { if (kind) next[pe] = kind; else delete next[pe]; }
      return { ...prev, [half]: next };
    });
  }, []);

  // A budget decision: kind 'model' | 'keep' | 'py' | 'typed'.
  const applyBudgetDecisions = useCallback(async (list, label = 'Saving') => {
    const built = [];
    for (const { job, kind, reason } of list) {
      const pe = job.PEJobIndex;
      const r = rowInfo[pe];
      if (!r) continue;
      const pred = predictions[pe];
      let tasks = null;
      if (kind === 'model' && r.ml?.split) tasks = r.ml.split.remaining;
      else if (kind === 'py' && r.py) tasks = r.py.map;
      else if (kind === 'typed') tasks = fullTaskMap(r.rows, edits[pe]?.tasks);
      // Keep normally saves just the total; when 199A was folded into Preparation, save the
      // tasks so the move reaches PE (same total).
      else if (kind === 'keep' && r.rows.some(t => t.Moved199A > 0)) tasks = fullTaskMap(r.rows);
      const review = {
        peJobIndex: pe, jobId: job.JobId, parts: ['budget'],
        source: budgetSource(kind, { pred, budget: r.base, appliedModelThisSession: kinds.budget[pe] === 'model' }),
        notes: pred ? mlNotes(pred, { reason }) : undefined,
      };
      if (tasks) review.taskEdits = Object.entries(tasks).map(([taskSubject, v]) => ({ taskSubject, newRemainingHrs: Number(v) }));
      else review.remainingHrs = r.baseRemaining;
      const after = tasks ? effectiveBudgetOf(job, r.rows, tasks) : { effectiveBudget: r.base, effectiveRemaining: r.baseRemaining };
      built.push({ pe, kind, tasks, review, newBudget: Number(after.effectiveBudget) || 0, newRemaining: Number(after.effectiveRemaining) || 0 });
    }
    if (built.length === 0) return;
    try {
      await saveReviews(built.map(b => b.review), label);
      for (const b of built) {
        patchJob(b.pe, j => ({
          ReviewStatus: j.ReviewStatus === 'submitted' ? j.ReviewStatus : 'draft',
          NewBudgetHours: b.newBudget, NewRemainingHrs: b.newRemaining,
        }));
        if (b.tasks) patchTasks(b.pe, b.tasks);
      }
      setEdits(prev => {
        let next = prev;
        for (const b of built) if (next[b.pe]?.tasks) next = { ...next, [b.pe]: withoutKey(next[b.pe], 'tasks') };
        return next;
      });
      for (const kind of new Set(built.map(b => b.kind))) setKind('budget', built.filter(b => b.kind === kind).map(b => b.pe), kind);
      refreshIDDProgress?.();
    } catch (err) {
      alert("Couldn't save: " + err.message);
    } finally {
      setBusy(null);
    }
  }, [rowInfo, predictions, edits, kinds, saveReviews, patchJob, patchTasks, setKind, refreshIDDProgress]);

  // A dates decision: kind 'ai' | 'keep' | 'typed'.
  const applyDatesDecisions = useCallback(async (list, { bulk = false, label = 'Saving' } = {}) => {
    const built = [];
    for (const { job, kind, startdate, enddate } of list) {
      const pe = job.PEJobIndex;
      if (!startdate || !enddate || String(enddate).slice(0, 10) <= String(startdate).slice(0, 10)) continue;
      const pred = predictions[pe];
      built.push({
        pe, kind, startdate, enddate,
        review: {
          peJobIndex: pe, jobId: job.JobId, parts: ['dates'], startDeadline: startdate, deadline: enddate,
          source: datesSource(kind, { bulk, acceptedAIThisSession: kinds.dates[pe] === 'ai' }),
          notes: pred ? mlNotes(pred) : undefined,
        },
      });
    }
    if (built.length === 0) return;
    try {
      await saveReviews(built.map(b => b.review), label);
      for (const b of built) {
        patchJob(b.pe, j => ({
          ReviewStatus: j.ReviewStatus === 'submitted' ? j.ReviewStatus : 'draft',
          NewStartDeadline: b.startdate, NewDeadline: b.enddate,
        }));
      }
      setEdits(prev => {
        let next = prev;
        for (const b of built) if (next[b.pe]) next = { ...next, [b.pe]: withoutKey(withoutKey(next[b.pe], 'startDeadline'), 'deadline') };
        return next;
      });
      for (const kind of new Set(built.map(b => b.kind))) setKind('dates', built.filter(b => b.kind === kind).map(b => b.pe), kind);
      refreshIDDProgress?.();
    } catch (err) {
      alert("Couldn't save: " + err.message);
    } finally {
      setBusy(null);
    }
  }, [predictions, kinds, saveReviews, patchJob, setKind, refreshIDDProgress]);

  // Undo one half (Change, or the bulk Undo). The server removes a draft when nothing is left.
  const undoHalves = useCallback(async (pes, half) => {
    const byPe = new Map(jobs.map(j => [j.PEJobIndex, j]));
    const list = pes.map(pe => byPe.get(pe)).filter(Boolean);
    if (list.length === 0) return;
    try {
      await saveReviews(list.map(job => ({
        peJobIndex: job.PEJobIndex, jobId: job.JobId, parts: [], [half === 'budget' ? 'clearBudget' : 'clearDates']: true, source: 'manual',
      })), 'Undoing');
      for (const { PEJobIndex: pe } of list) {
        patchJob(pe, j => {
          const p = half === 'budget' ? { NewBudgetHours: null, NewRemainingHrs: null } : { NewStartDeadline: null, NewDeadline: null };
          const left = half === 'budget' ? (j.NewStartDeadline || j.NewDeadline) : j.NewBudgetHours != null;
          return { ...p, ReviewStatus: left ? j.ReviewStatus : null };
        });
        if (half === 'budget') patchTasks(pe, null);
      }
      setKind(half, list.map(j => j.PEJobIndex), null);
      refreshIDDProgress?.();
    } catch (err) {
      alert("Couldn't undo: " + err.message);
    } finally {
      setBusy(null);
    }
  }, [jobs, saveReviews, patchJob, patchTasks, setKind, refreshIDDProgress]);
  const undoHalf = useCallback((pe, half) => undoHalves([pe], half), [undoHalves]);

  const onDecideBudget = useCallback((pe, option, { reason } = {}) => {
    const job = jobs.find(j => j.PEJobIndex === pe);
    if (job && option) applyBudgetDecisions([{ job, kind: option.kind, reason }]);
  }, [jobs, applyBudgetDecisions]);
  const onSaveTypedBudget = useCallback((pe) => {
    const job = jobs.find(j => j.PEJobIndex === pe);
    if (job) applyBudgetDecisions([{ job, kind: 'typed' }]);
  }, [jobs, applyBudgetDecisions]);
  const onDecideDates = useCallback((pe, option) => {
    const job = jobs.find(j => j.PEJobIndex === pe);
    if (job && option) applyDatesDecisions([{ job, kind: option.kind, startdate: option.startdate, enddate: option.enddate }]);
  }, [jobs, applyDatesDecisions]);
  const onSaveTypedDates = useCallback((pe) => {
    const job = jobs.find(j => j.PEJobIndex === pe);
    if (!job) return;
    const e = edits[pe] || {};
    const s = e.startDeadline ?? job.NewStartDeadline ?? job.StartDeadline;
    const d = e.deadline ?? job.NewDeadline ?? job.Deadline;
    applyDatesDecisions([{ job, kind: 'typed', startdate: s ? String(s).slice(0, 10) : null, enddate: d ? String(d).slice(0, 10) : null }]);
  }, [jobs, edits, applyDatesDecisions]);

  const onEditTask = useCallback((pe, subject, value) => {
    setEdits(prev => ({ ...prev, [pe]: { ...prev[pe], tasks: { ...(prev[pe]?.tasks || {}), [subject]: value } } }));
  }, []);
  const onEditDate = useCallback((pe, field, value) => {
    setEdits(prev => ({ ...prev, [pe]: { ...prev[pe], [field]: value } }));
  }, []);
  const onCancelBudgetTyping = useCallback((pe) => setEdits(prev => (prev[pe] ? { ...prev, [pe]: withoutKey(prev[pe], 'tasks') } : prev)), []);
  const onCancelDateTyping = useCallback((pe) => setEdits(prev => (prev[pe] ? { ...prev, [pe]: withoutKey(withoutKey(prev[pe], 'startDeadline'), 'deadline') } : prev)), []);

  // ── Bulk (ticked rows) ─────────────────────────────────────────────────
  // The main action gives each ticked row its OWN recommendation (its first button), so
  // rows that want different things are still one click. The single actions stay as small
  // links, for "keep all of mine" or "use the model on all of these".
  const selectedJobs = useMemo(() => tableJobs.filter(j => selection.has(j.PEJobIndex)), [tableJobs, selection]);
  const bulkStep = activeStep === 'dates' ? 'dates' : 'budgets';
  const optionsOf = useCallback((pe) => {
    const r = rowInfo[pe];
    return r ? (bulkStep === 'dates' ? r.dateOptions : r.options) : [];
  }, [rowInfo, bulkStep]);
  const bulkHalf = bulkStep === 'dates' ? 'dates' : 'budget';
  const plan = useMemo(() => recommendedPlan(selectedJobs.map(j => ({
    pe: j.PEJobIndex, options: optionsOf(j.PEJobIndex), decided: bulkHalf === 'dates' ? datesDecided(j) : budgetDecided(j),
  }))), [selectedJobs, optionsOf, bulkHalf]);
  const planWords = useMemo(() => describePlan(plan, bulkStep), [plan, bulkStep]);
  const applyPicks = useCallback(async (picks) => {
    if (bulkStep === 'dates') {
      await applyDatesDecisions(picks.map(({ job, option }) => ({ job, kind: option.kind, startdate: option.startdate, enddate: option.enddate })),
        { bulk: true, label: 'Saving dates' });
    } else {
      await applyBudgetDecisions(picks.map(({ job, option }) => ({ job, kind: option.kind })), 'Saving budgets');
    }
    setSelection(new Set());
  }, [bulkStep, applyDatesDecisions, applyBudgetDecisions]);
  const runRecommended = useCallback(async () => {
    const n = plan.picks.length;
    const msg = `Apply the recommended ${bulkStep === 'dates' ? 'dates' : 'budget'} to ${n} job${n !== 1 ? 's' : ''}?\n\n`
      + planWords.mix.map(m => `• ${m}`).join('\n')
      + (planWords.skipped.length ? `\n\nSkipped: ${planWords.skipped.join('; ')}.` : '');
    if (!window.confirm(msg)) return;
    const byPe = new Map(selectedJobs.map(j => [j.PEJobIndex, j]));
    await applyPicks(plan.picks.map(p => ({ job: byPe.get(p.pe), option: p.option })));
  }, [plan, planWords, bulkStep, selectedJobs, applyPicks]);
  const eligible = useCallback((kind) => selectedJobs.filter(j => optionsOf(j.PEJobIndex).some(o => o.kind === kind)), [selectedJobs, optionsOf]);
  const runBulk = useCallback(async (kind) => {
    const list = eligible(kind);
    const skipped = selectedJobs.length - list.length;
    const what = {
      model: "use the model's budget on", keep: 'keep the current budget on', py: "use last year's hours on",
      ai: "accept Claude's dates on", keepDates: 'keep the current dates on',
    }[bulkStep === 'dates' && kind === 'keep' ? 'keepDates' : kind];
    const msg = `${what[0].toUpperCase()}${what.slice(1)} ${list.length} job${list.length !== 1 ? 's' : ''}?`
      + (skipped > 0 ? `\n\n${skipped} of the selected job${skipped !== 1 ? 's' : ''} can't take this and will be skipped.` : '');
    if (!window.confirm(msg)) return;
    await applyPicks(list.map(job => ({ job, option: optionsOf(job.PEJobIndex).find(o => o.kind === kind) })));
  }, [eligible, selectedJobs, bulkStep, optionsOf, applyPicks]);
  // Ticked jobs already decided can be undone together (Hutson, 2026-10-20: Show Decided,
  // tick all, Undo), as Change does one at a time.
  const undoList = useMemo(() => selectedJobs.filter(j => undoable(j, bulkHalf)), [selectedJobs, bulkHalf]);
  const runUndo = useCallback(async () => {
    const n = undoList.length;
    const msg = `Undo the ${bulkHalf === 'dates' ? 'dates' : 'budget'} decision on ${n} job${n !== 1 ? 's' : ''}? They go back on the list to decide.\n\n`
      + (bulkHalf === 'dates' ? 'Their budgets stay as decided.' : 'Any dates you decided stay as they are.');
    if (!window.confirm(msg)) return;
    await undoHalves(undoList.map(j => j.PEJobIndex), bulkHalf);
    setSelection(new Set());
  }, [undoList, bulkHalf, undoHalves]);
  // With only decided jobs ticked, Undo is the only thing to do.
  const anyToDecide = plan.picks.length + plan.noSuggestion > 0;
  const bulkPrimary = anyToDecide ? {
    label: `Apply recommended (${plan.picks.length})`, count: plan.picks.length, tone: bulkStep === 'dates' ? 'dates' : 'budget',
    title: [planWords.mix.join(', '), planWords.skipped.length ? `Skipped: ${planWords.skipped.join('; ')}` : ''].filter(Boolean).join(' · '),
    onClick: runRecommended,
  } : null;
  const bulkOthers = !anyToDecide ? [] : (bulkStep === 'dates'
    ? [['ai', 'Accept AI dates'], ['keep', 'Keep current dates']]
    : [['model', 'Use model'], ['keep', 'Keep current'], ['py', 'Use last year']]
  ).map(([key, label]) => {
    const count = eligible(key).length;
    return { key, label, count, title: `${count} of the ${selectedJobs.length} ticked jobs can take this`, onClick: () => runBulk(key) };
  });
  const bulkUndo = undoList.length > 0 ? {
    label: `Undo ${undoList.length === 1 ? 'decision' : 'decisions'} (${undoList.length})`, onClick: runUndo,
    title: `Take back the ${bulkHalf === 'dates' ? 'dates' : 'budget'} decision on the ${undoList.length} ticked job${undoList.length !== 1 ? 's' : ''} already decided`,
  } : null;
  const toggleSelect = useCallback((pe) => setSelection(prev => {
    const next = new Set(prev);
    if (next.has(pe)) next.delete(pe); else next.add(pe);
    return next;
  }), []);

  // ── Whole-row actions (the ⋯ menu) ─────────────────────────────────────
  const [historyJobPe, setHistoryJobPe] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const clearRowLocal = useCallback((pe) => {
    setEdits(prev => withoutKey(prev, pe));
    setKind('budget', [pe], null);
    setKind('dates', [pe], null);
  }, [setKind]);
  const resetRow = (j) => ({
    ...j, ReviewStatus: null, ReviewId: null, NewStartDeadline: null, NewDeadline: null, NewRemainingHrs: null,
    NewBudgetHours: null, AIStart: null, AIEnd: null, AIHours: null, AIReason: null, AIAccepted: false,
    SubmittedAt: null, RemoveAction: null,
  });

  const handleClearDraft = useCallback(async (pe, confirmText) => {
    const cn = cycleName || 'default';
    if (!window.confirm(confirmText || 'Clear everything saved on this job for the cycle (its budget and dates decisions)?')) return;
    try {
      await clearIDDDraft(pe, cn);
      clearRowLocal(pe);
      setJobs(prev => prev.map(j => (j.PEJobIndex === pe ? resetRow(j) : j)));
      try {
        const fresh = await fetchJobTaskBudgets(pe, cn);
        setTaskCachePatch(prev => ({ ...prev, [pe]: fresh }));
      } catch (_) { /* non-fatal */ }
      refreshIDDProgress?.();
    } catch (err) {
      alert('Failed to clear: ' + err.message);
    }
  }, [cycleName, clearRowLocal, refreshIDDProgress]);

  const handleRemoveJob = useCallback(async (pe, removeAction) => {
    const cn = cycleName || 'default';
    const job = jobs.find(j => j.PEJobIndex === pe);
    if (!job) return;
    try {
      await removeIDDJob({ peJobIndex: pe, jobId: job.JobId, cycleName: cn, removeAction });
      patchJob(pe, { ReviewStatus: 'confirmed', RemoveAction: removeAction });
      setEdits(prev => withoutKey(prev, pe));
      refreshIDDProgress?.();
      setRemoveModal(null);
    } catch (err) {
      alert('Cannot remove: ' + err.message);
    }
  }, [cycleName, jobs, patchJob, refreshIDDProgress]);

  const menuFor = useCallback((job) => {
    const pe = job.PEJobIndex;
    const items = [];
    if (job.ReviewStatus) items.push({ label: 'History', onClick: () => setHistoryJobPe(pe), title: 'Every change to this job this cycle, and restore' });
    if (readOnly || job.ReviewStatus === 'submitted') return items;
    if (job.RemoveAction) {
      items.push({ label: 'Undo remove', onClick: () => handleClearDraft(pe, 'Put this job back on your list? It will need a budget and dates again.') });
    } else if (job.ReviewStatus === 'confirmed') {
      items.push({ label: 'Undo confirm', onClick: () => handleClearDraft(pe, 'Undo the confirm? The job goes back to needing a budget and dates.') });
    } else {
      items.push({ label: 'Remove job…', onClick: () => setRemoveModal({ peJobIndex: pe, code: job.ClientCode, choice: null }), title: "Not doing it this year, or close it and don't roll it forward" });
      if (job.ReviewStatus === 'draft') items.push({ label: 'Clear saved changes', danger: true, onClick: () => handleClearDraft(pe) });
    }
    return items;
  }, [readOnly, handleClearDraft]);

  // ── Non-billable status (the ✓ on its subtab) ──────────────────────────
  const [nonBillSubmitted, setNonBillSubmitted] = useState(false);
  const refreshNonBillStatus = useCallback(async () => {
    if (!myStaffId) { setNonBillSubmitted(false); return; }
    try {
      const r = await fetchNonBillProgressSummary(myStaffId);
      setNonBillSubmitted(!!r?.isSubmitted);
    } catch (_) { /* non-blocking */ }
  }, [myStaffId]);
  useEffect(() => { refreshNonBillStatus(); }, [refreshNonBillStatus]);

  // ── Submit ─────────────────────────────────────────────────────────────
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingCount, setSubmittingCount] = useState(0);
  const [submittingBatch, setSubmittingBatch] = useState(null);
  const submittable = useMemo(() => myJobs.filter(j => j.ReviewStatus === 'draft' || j.ReviewStatus === 'confirmed'), [myJobs]);
  const notReady = useMemo(() => myJobs.filter(j => !jobReady(j)).map(job => ({ job, missing: missingParts(job) })), [myJobs]);

  const confirmFinalize = useCallback(async () => {
    const cn = cycleName || 'default';
    const peJobIndexes = submittable.map(j => j.PEJobIndex);
    setFinalizeModalOpen(false);
    if (peJobIndexes.length === 0) { alert('Nothing to submit yet.'); return; }
    setSubmitting(true);
    setSubmittingCount(peJobIndexes.length);
    // Chunks keep each call under the SWA gateway timeout (~25 jobs, ~15 s); one retry on 5xx.
    const CHUNK_SIZE = 25;
    const chunks = [];
    for (let i = 0; i < peJobIndexes.length; i += CHUNK_SIZE) chunks.push(peJobIndexes.slice(i, i + CHUNK_SIZE));
    async function postChunkWithRetry(chunk) {
      try {
        return await submitIDDReview({ cycleName: cn, peJobIndexes: chunk });
      } catch (err) {
        if (!/\b5\d\d\b/.test(err.message || '')) throw err;
        await new Promise(res => setTimeout(res, 2000));
        return submitIDDReview({ cycleName: cn, peJobIndexes: chunk });
      }
    }
    try {
      let allResults = [];
      let processed = 0;
      for (let i = 0; i < chunks.length; i++) {
        setSubmittingBatch({ index: i + 1, total: chunks.length, from: processed + 1, to: processed + chunks[i].length, totalJobs: peJobIndexes.length });
        const result = await postChunkWithRetry(chunks[i]);
        allResults = allResults.concat(result.results || []);
        processed += chunks[i].length;
      }
      const failed = allResults.filter(r => r.status === 'failed');
      if (failed.length > 0) {
        alert(`${failed.length} job(s) failed to submit. Check console for details.`);
        console.warn('Submit failures:', failed);
      }
    } catch (err) {
      alert('Submit failed: ' + err.message);
    } finally {
      // Refresh whatever happened, so a partial failure doesn't leave stale drafts on screen.
      try { await reloadJobs(cn); } catch (_) {}
      try { refreshIDDProgress?.(); } catch (_) {}
      setSubmitting(false);
      setSubmittingBatch(null);
    }
  }, [cycleName, submittable, reloadJobs, refreshIDDProgress]);

  // getEffective for the timeline + histogram: typed > saved > PE.
  const getEffective = useCallback((job, field) => {
    const e = edits[job.PEJobIndex];
    if (e && e[field] != null) return e[field];
    switch (field) {
      case 'startDeadline': return job.NewStartDeadline || job.StartDeadline;
      case 'deadline': return job.NewDeadline || job.Deadline;
      case 'remainingHrs': return job.NewRemainingHrs != null ? job.NewRemainingHrs : job.RemainingHours;
      // The decided budget (step 1), else PE's.
      case 'budgetHours': return job.NewBudgetHours != null ? job.NewBudgetHours : job.BudgetHours;
      default: return null;
    }
  }, [edits]);
  // The timeline only shows suggestions for jobs whose dates aren't decided.
  const aiForTimeline = useMemo(() => {
    const out = {};
    for (const job of filteredUnsorted) if (aiSuggestions[job.PEJobIndex] && !datesDecided(job)) out[job.PEJobIndex] = aiSuggestions[job.PEJobIndex];
    return out;
  }, [filteredUnsorted, aiSuggestions]);

  // ── Render ─────────────────────────────────────────────────────────────
  if (!ready) return null;

  // Non-admins outside an active cycle have nothing to do here: point them at their
  // year-round non-billable view. Dev users pass, to reach a QA sandbox cycle.
  const noActiveCycle = !cycles.find(c => c.IsActive);
  if (!isSuperUser && !isDevSuperUser && noActiveCycle && !loading) {
    return (
      <div className="idd-root" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="idd-placeholder" style={{ maxWidth: 560, textAlign: 'center', padding: 32 }}>
          <h3 style={{ marginTop: 0 }}>No active data integrity cycle</h3>
          <p style={{ color: '#6B7280', marginBottom: 16 }}>
            Data Integrity reviews are only open while an admin has a cycle active. You can review your
            non-billable budget allocation and progress year-round on your profile page.
          </p>
          {myStaffId && (
            <a className="idd-btn idd-btn--primary" href={`/schedule/employee/${myStaffId}?tab=nonbill`}>
              Go to my profile → Non-Billable
            </a>
          )}
        </div>
      </div>
    );
  }

  const summary = cycleSummary(currentCycle);
  const toDecideLabel = activeStep === 'dates' ? 'Dates to decide' : 'Budgets to decide';

  return (
    <div className="idd-root">
      {/* ── Subtabs + the cycle, in words ── */}
      <div className="sched-tabs idd-subtab-row" style={{ flexShrink: 0 }}>
        <div className="idd-subtab-tabs">
          {[
            { key: 'idd', label: 'Budgets & Dates', done: allSubmitted },
            { key: 'nonbill', label: 'Non-Billable Budget Hours', done: nonBillSubmitted },
            ...(isSuperUser ? [{ key: 'admin', label: 'Admin' }] : []),
          ].map(t => (
            <button
              key={t.key}
              className={`sched-tab ${subTab === t.key ? 'sched-tab--active' : ''} ${t.done ? 'sched-tab--complete' : ''}`}
              onClick={() => {
                if (subTab === 'nonbill' && t.key !== 'nonbill') refreshNonBillStatus();
                setSubTab(t.key);
              }}
            >
              {t.label}
              {t.done && <span className="sched-tab-complete-check" aria-label="complete">✓</span>}
            </button>
          ))}
        </div>
        {subTab === 'idd' && (
          <div className="idd-subtab-cycle-info">
            {isAdminish && cycles.length > 0 ? (
              <select
                className="idd-select idd-subtab-cycle-select"
                value={cycleName}
                onChange={e => { const v = e.target.value; setCycleName(v); setCycleParam(v); reloadJobs(v || 'default'); }}
                title="Cycle"
              >
                <option value="">No cycle</option>
                {cycles.map(c => (
                  <option key={c.CycleId} value={c.CycleName}>{c.CycleName}{c.IsActive ? ' (active)' : ''}</option>
                ))}
              </select>
            ) : currentCycle && <span className="idd-cycle-name">{currentCycle.CycleName}</span>}
            {summary && <span className="idd-cycle-filter-hint-chip" title="Which jobs are in this cycle">{summary}</span>}
          </div>
        )}
      </div>

      {subTab === 'nonbill' && <NonBillBudgetTab myStaffId={myStaffId} />}

      {subTab === 'admin' && (
        isSuperUser ? (
          <DataIntegrityAdminTab
            cycles={cycles}
            onCyclesChanged={reloadCycles}
            onActivateCycle={(name) => { setCycleName(name); setCycleParam(name); reloadJobs(name); setSubTab('idd'); }}
          />
        ) : <div className="idd-placeholder" style={{ flex: 1 }}>Admin access required</div>
      )}

      {subTab === 'idd' && (loading ? (
        <div className="sched-home-loading"><div className="sched-home-loading-text">Loading your jobs...</div></div>
      ) : error ? (
        <div style={{ padding: 24, color: '#DC2626' }}>Error: {error}</div>
      ) : (
        <>
          <div className="idd-steps-row">
            {myJobs.length > 0 ? (
              <IDDStepBar step={activeStep} onStep={goStep} counts={counts} allSubmitted={allSubmitted} datesLock={readOnly ? null : datesLock} />
            ) : <div className="idd-cell-sub" style={{ padding: '8px 4px' }}>{canActAsAny ? 'Pick a manager to see their steps.' : 'You have no jobs in this cycle.'}</div>}
            <button className="idd-help-btn" onClick={() => setHelpOpen(true)} title="How this page works" aria-label="Open help">?</button>
          </div>

          {noCycle && (
            <div className="idd-ml-banner idd-ml-banner--stub">
              <strong>NO CYCLE SELECTED</strong>
              <span>Pick a cycle in the top right ({cycles.find(c => c.IsQA) ? `${cycles.find(c => c.IsQA).CycleName} for testing` : 'the open cycle'}) to see its AI dates and make decisions. Nothing can be saved without one.</span>
            </div>
          )}
          {isQACycle && (
            <div className="idd-ml-banner idd-ml-banner--qa">
              <strong>QA SANDBOX</strong>
              <span>
                <strong>{cycleName}</strong> is a test copy of the cycle
                {currentCycle?.LocalActive ? ', open on this machine only (IDD_LOCAL_ACTIVE_CYCLE)' : ''}.
                Saves land only here, Submit is a dry run (nothing reaches Practice Engine), and the whole cycle is wiped after testing.
                {canActAsAny ? ' Pick any manager in the filter to test their rows.' : ''}
              </span>
            </div>
          )}
          {stubInView && (
            <div className="idd-ml-banner idd-ml-banner--stub">
              <strong>STUB DATA</strong>
              <span>The model suggestions on this page are placeholders (last year's hours), not the model. Use them to test the screens only; don't budget from them.</span>
            </div>
          )}
          {allSubmitted && (
            <div className="idd-readonly-banner">
              <strong>Submitted ✓</strong>
              <span>Your budgets and dates for <strong>{cycleName || 'this cycle'}</strong> are in. Contact an admin to reopen this view if changes are needed.</span>
            </div>
          )}

          {activeStep !== 'submit' && (
            <div className="idd-filter-bar">
              <div className="idd-filter-left">
                <span className="idd-show-label">Show:</span>
                {[
                  { key: 'todo', label: toDecideLabel, n: showCounts.todo },
                  { key: 'done', label: 'Decided', n: showCounts.done },
                  { key: 'all', label: 'All', n: showCounts.all },
                ].map(c => (
                  <button key={c.key} className={`idd-ml-filter-chip${show === c.key ? ' idd-ml-filter-chip--active' : ''}`} onClick={() => setShow(c.key)}>
                    {c.label}<span className="idd-ml-filter-count">{c.n}</span>
                  </button>
                ))}
                {canActAsAny && (
                  <select className="idd-select" value={managerFilter} title="Whose jobs to show (and act for)"
                    onChange={e => { setManagerFilter(e.target.value); setSelection(new Set()); setSelectedJob(null); setHoveredJob(null); }}>
                    <option value="mine">My jobs</option>
                    <option value="all">All managers</option>
                    {managerList.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                )}
                {officeList.length > 1 && (
                  <select className="idd-select" value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
                    <option value="all">All offices</option>
                    {officeList.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
                {deptList.length > 1 && (
                  <select className="idd-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                    <option value="all">All departments</option>
                    {deptList.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}
              </div>
              <div className="idd-filter-right">
                <input className="idd-search" type="text" placeholder="Search client or job..." value={search} onChange={e => setSearch(e.target.value)} />
                <span className="idd-count">{tableJobs.length} shown</span>
              </div>
            </div>
          )}

          {activeStep === 'budgets' && (
            <div className="idd-ml-filter-row">
              <span className="idd-ml-filter-label" title="Narrow the list">Filter:</span>
              {[
                { key: 'all', label: 'Any' },
                { key: 'no-budget', label: 'No budget', title: "Jobs with no budget yet, of any kind. Model jobs offer the model's number; others last year's hours or Enter budget." },
                ...(anyPredictions ? [
                  { key: 'outside', label: 'Well off the model', outside: true, title: 'Stale roll-forwards + budgets set this year that the model thinks are well off. Work these first.' },
                  { key: 'stale', label: 'Stale roll-forward', title: "Same budget as last year's final budget, and well off the model." },
                  { key: 'set-this-year', label: 'Differs', title: 'Budget set this year, well off the model.' },
                  { key: 'close', label: 'Near model', title: "Inside the model's wider (80%) range, but more than 2h or 10% off its number." },
                  { key: 'agrees', label: 'Agrees', title: "Within 2h or 10% of the model's number (whichever is bigger)." },
                  { key: 'none', label: 'No model', title: "No model suggestion (not a returning 1065 / 1120 / 1120-S, or the return type changed). Last year's hours are offered instead." },
                ] : []),
              ].map(c => (
                <button
                  key={c.key}
                  className={`idd-ml-filter-chip${mlFilter === c.key ? ' idd-ml-filter-chip--active' : ''}${c.outside ? ' idd-ml-filter-chip--outside' : ''}`}
                  onClick={() => setMlFilter(c.key)}
                  title={c.title}
                >
                  {c.label}<span className="idd-ml-filter-count">{mlCounts[c.key] ?? 0}</span>
                </button>
              ))}
              {!tasksReady && <span className="idd-ml-filter-note">counting...</span>}
            </div>
          )}

          {activeStep === 'dates' && (
            <>
              {datesLock.locked && !readOnly && (
                <div className="idd-dates-lock" role="status">
                  <strong>Dates unlock when every job has a budget.</strong>
                  <span>
                    {datesLock.pending} job{datesLock.pending !== 1 ? 's' : ''} still need{datesLock.pending === 1 ? 's' : ''} one.
                    Then the AI dates are re-planned with your budgets, so they fit the hours you chose.
                  </span>
                  <button className="idd-dec-btn idd-dec-btn--budget idd-dec-btn--primary" onClick={() => goStep('budgets')}>Go to Budgets</button>
                </div>
              )}
              <div className="idd-ai-bar">
                <span className="idd-ai-bar-text">
                  {aiInfo && aiInfo.count > 0
                    ? <>AI dates {aiInfo.kind === 'precompute' ? 'pre-computed' : 'made'} {fmtDate(aiInfo.at)} for {aiInfo.count} jobs.</>
                    : 'No AI dates yet.'}
                  {staleAI.size > 0 && <strong className="idd-ai-bar-stale"> {staleAI.size} budget{staleAI.size !== 1 ? 's' : ''} changed since; refresh to re-plan with them.</strong>}
                  {aiInfo?.hitCap && <span className="idd-ai-truncation-warning"> Some jobs may be missing; try again.</span>}
                </span>
                {!datesReadOnly && (
                  <>
                    {isAdminish && (
                      <select className="idd-select" value={aiModel} onChange={e => setAiModel(e.target.value)} style={{ width: 90 }} title="AI model (admins)">
                        <option value="">Sonnet</option>
                        <option value="claude-opus-5-5">Opus</option>
                      </select>
                    )}
                    <button className={`idd-dec-btn idd-dec-btn--dates${staleAI.size > 0 || !aiInfo ? ' idd-dec-btn--primary' : ''}`}
                      onClick={handleAIRecommend} disabled={aiRunning}
                      title="Re-plan your dates with your current budgets (a minute or two)">
                      {aiRunning ? 'Working…' : aiInfo ? 'Refresh AI dates' : 'Get AI dates'}
                    </button>
                  </>
                )}
              </div>
              <IDDTimeline
                jobs={filteredUnsorted}
                edits={edits}
                getEffective={getEffective}
                selectedJob={selectedJob}
                onSelectJob={setSelectedJob}
                aiSuggestions={aiForTimeline}
                zoom={timelineZoom}
                onZoomChange={setTimelineZoom}
                externalScrollRef={timelineScrollRef}
                onHorizontalScroll={handleTimelineScroll}
                cutoffDate={extensionCutoff(currentCycle?.ScheduleUpTo) || currentCycle?.ScheduleUpTo || null}
                windowMode={chartWindowMode}
                onWindowModeChange={setChartWindowMode}
                hoveredJob={hoveredJob}
                onHoverJob={setHoveredJob}
              />
              <IDDHistogram
                jobs={filteredUnsorted}
                edits={edits}
                getEffective={getEffective}
                pyWeeklyData={pyWeeklyData}
                focusJobPYWeekly={focusJobPYWeekly}
                zoom={timelineZoom}
                externalScrollRef={histogramScrollRef}
                onHorizontalScroll={handleHistogramScroll}
                aiSuggestions={aiForTimeline}
                cutoffDate={extensionCutoff(currentCycle?.ScheduleUpTo) || currentCycle?.ScheduleUpTo || null}
                windowMode={chartWindowMode}
              />
            </>
          )}

          <div className="idd-content">
            {activeStep !== 'submit' && !tasksReady ? (
              <div className="sched-home-loading"><div className="sched-home-loading-text">Loading task budgets...</div></div>
            ) : activeStep === 'budgets' ? (
              <IDDBudgetsTable
                jobs={tableJobs} info={rowInfo} edits={edits}
                selection={selection} onToggle={toggleSelect} onToggleAll={pes => setSelection(new Set(pes))}
                expanded={selectedJob}
                onExpand={(pe, open) => setSelectedJob(prev => (open ? pe : prev === pe ? null : pe))}
                onDecide={onDecideBudget} onSaveTyped={onSaveTypedBudget} onCancelTyped={onCancelBudgetTyping}
                onEditTask={onEditTask} onUndo={pe => undoHalf(pe, 'budget')}
                onNotNeeded={pe => { const job = jobs.find(j => j.PEJobIndex === pe); if (job) setRemoveModal({ peJobIndex: pe, code: job.ClientCode, choice: null }); }}
                kinds={kinds.budget} readOnly={readOnly} showManager={canActAsAny}
                menuFor={menuFor} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} busy={!!busy}
              />
            ) : activeStep === 'dates' ? (
              <IDDDatesTable
                jobs={tableJobs} info={rowInfo} edits={edits} ai={aiSuggestions} staleAI={staleAI}
                selection={selection} onToggle={toggleSelect} onToggleAll={pes => setSelection(new Set(pes))}
                selectedJob={selectedJob} onSelect={pe => setSelectedJob(prev => (prev === pe ? null : pe))}
                focusJob={focusJob} onHover={setHoveredJob}
                onDecide={onDecideDates} onSaveTyped={onSaveTypedDates} onCancelTyped={onCancelDateTyping}
                onEditDate={onEditDate} onUndo={pe => undoHalf(pe, 'dates')} onGoBudget={pe => goStep('budgets', pe)}
                kinds={kinds.dates} readOnly={datesReadOnly} showManager={canActAsAny}
                menuFor={menuFor} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} busy={!!busy}
              />
            ) : (
              <IDDSubmitPanel
                counts={counts} notReady={notReady} onFix={goStep}
                onSubmit={() => setFinalizeModalOpen(true)} allSubmitted={allSubmitted}
                busy={submitting || !!busy || noCycle} cycleName={cycleName} isQA={isQACycle}
              />
            )}
          </div>

          {activeStep !== 'submit' && (
            <IDDBulkBar selectedCount={selectedJobs.length} primary={bulkPrimary} others={bulkOthers} undo={bulkUndo} onClear={() => setSelection(new Set())} busy={busy} />
          )}
          {busy && selection.size === 0 && <div className="idd-saving-toast" role="status">{busy}</div>}
        </>
      ))}

      {aiRunning && (
        <div className="idd-ai-modal-overlay">
          <div className="idd-ai-modal">
            <img className="idd-ai-modal-loader" src={LOADER_GIF} alt="Loading" />
            <div className="idd-ai-modal-title">{autoRerun ? 'Re-planning your dates with the budgets you decided' : 'Planning your dates'}</div>
            <div className="idd-ai-modal-joke">{aiJoke}</div>
            <div className="idd-ai-modal-note">This takes a minute or two for a long job list.</div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="idd-ai-modal-overlay">
          <div className="idd-ai-modal">
            <img className="idd-ai-modal-loader" src={LOADER_GIF} alt="Submitting" />
            <div className="idd-ai-modal-title">Submitting to Practice Engine</div>
            <div className="idd-ai-modal-joke">
              {submittingBatch && submittingBatch.total > 1
                ? `Sending batch ${submittingBatch.index} of ${submittingBatch.total} (jobs ${submittingBatch.from}–${submittingBatch.to} of ${submittingBatch.totalJobs}). Sit tight.`
                : `Sending ${submittingCount} job${submittingCount !== 1 ? 's' : ''}. Sit tight.`}
            </div>
            <div className="idd-ai-modal-note">Each job writes its dates and budget to Practice Engine. Don't refresh.</div>
          </div>
        </div>
      )}

      <IDDHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} cycle={currentCycle} jobCount={myJobs.length || null} />

      {historyJobPe && (() => {
        const job = jobs.find(j => j.PEJobIndex === historyJobPe);
        const cn = cycleName || 'default';
        return (
          <IDDHistoryModal
            open={!!historyJobPe}
            onClose={() => setHistoryJobPe(null)}
            peJobIndex={historyJobPe}
            cycleName={cn}
            jobLabel={job ? `${job.ClientCode || ''} ${job.JobName || ''}`.trim() : null}
            currentStatus={job?.ReviewStatus}
            onRevertSuccess={async (row) => {
              const pe = historyJobPe;
              clearRowLocal(pe);
              // revertAudit deletes the review (reverting to a clear) or upserts it as a draft.
              setJobs(prev => prev.map(j => {
                if (j.PEJobIndex !== pe) return j;
                if (row.Action === 'clear_draft') return resetRow(j);
                return {
                  ...j, ReviewStatus: 'draft',
                  NewStartDeadline: row.NewStartDeadline ?? null, NewDeadline: row.NewDeadline ?? null,
                  NewBudgetHours: row.NewBudgetHours ?? null, NewRemainingHrs: row.NewRemainingHrs ?? null,
                  SubmittedAt: null,
                };
              }));
              try {
                const fresh = await fetchJobTaskBudgets(pe, cn);
                setTaskCachePatch(prev => ({ ...prev, [pe]: fresh }));
              } catch (_) { /* non-fatal */ }
              refreshIDDProgress?.();
            }}
          />
        );
      })()}

      {removeModal && (
        <div className="idd-finalize-modal-overlay" onClick={() => setRemoveModal(null)}>
          <div className="idd-finalize-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Remove job</h3>
            <p style={{ fontSize: 13, color: '#4B5563', margin: '0 0 14px' }}>
              Take <strong>{removeModal.code}</strong> off your list for this cycle. Your choice is recorded so Practice Engine can be updated.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 0 18px' }}>
              {[
                { key: 'not_doing_cy', title: 'Not doing it this year', text: 'The job is deferred. Its Practice Engine status becomes "Not Doing CY" and it rolls forward to a future cycle.', bg: '#FEF3C7' },
                { key: 'close_no_roll', title: "Close it and don't roll it forward", text: "The job is permanently done. Practice Engine closes it and it won't roll into the next cycle.", bg: '#FECACA' },
              ].map(o => (
                <label key={o.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '6px 8px', border: '1px solid #E5E7EB', borderRadius: 6, background: removeModal.choice === o.key ? o.bg : '#fff' }}>
                  <input type="radio" name="removeAction" checked={removeModal.choice === o.key}
                    onChange={() => setRemoveModal(s => ({ ...s, choice: o.key }))} style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{o.title}</div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{o.text}</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="idd-btn idd-btn--secondary" onClick={() => setRemoveModal(null)}>Cancel</button>
              <button className="idd-btn idd-btn--primary" disabled={!removeModal.choice}
                onClick={() => handleRemoveJob(removeModal.peJobIndex, removeModal.choice)}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {finalizeModalOpen && (
        <div className="idd-finalize-modal-overlay" onClick={() => setFinalizeModalOpen(false)}>
          <div className="idd-finalize-modal" onClick={ev => ev.stopPropagation()}>
            <div className="idd-finalize-modal-title">Submit your budgets and dates?</div>
            <div className="idd-finalize-modal-body">
              <p>For <strong>{cycleName || 'this cycle'}</strong>, this will:</p>
              <ul>
                <li>Send the budgets and internal due dates for <strong>{submittable.length} job{submittable.length !== 1 ? 's' : ''}</strong> to Practice Engine{isQACycle ? ' (QA sandbox: a dry run, nothing is sent)' : ''}</li>
                <li>Make this page read-only for you</li>
              </ul>
              <p>Afterwards only an admin can reopen it.</p>
            </div>
            <div className="idd-finalize-modal-actions">
              <button className="idd-btn idd-btn--secondary" onClick={() => setFinalizeModalOpen(false)}>Cancel</button>
              <button className="idd-btn idd-btn--submit" onClick={confirmFinalize}>Yes, submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sort value extractor ─────────────────────────────────────────────────
function getVal(job, col, info) {
  switch (col) {
    case 'clientCode': return job.ClientCode || '';
    case 'jobName': return job.JobName || '';
    case 'manager': return job.ManagerName || '';
    case 'budget': {
      const r = info?.[job.PEJobIndex];
      return r ? (r.decidedHours ?? r.base) : (job.BudgetHours ?? 0);
    }
    default: return '';
  }
}
